import { type ReactNode, useId, useState } from "react";
import { Link } from "react-router-dom";
import { usePublish } from "../api/models";
import type { ModelDetail, ModelDressing } from "../api/types";
import { rowMessageFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Dialog, Field, Input } from "../ui";
import { WAIT_TITLE } from "./HistoryPanel";
import { ReviewPictures, focusTryAgain } from "./SculptedPanel";

/** The object's name: the design's, cut to this. */
export const OBJECT_NAME_MAX = 60;
export const ADD_TITLE = "Add to Objects";
export const UPDATE_TITLE = "Update in Objects";
export const ADDING = "Adding…";
export const ADD_TEXT =
  "Ads can use it from now on. You can keep changing the design; nothing changes in Objects until you press Update.";
export const ADD_ARTWORK_TEXT =
  "Artwork you placed here is not saved with the object. Press View it afterwards: the object opens wearing it, ready for Save this look.";
export const IN_LIBRARY = "In Objects.";
export const UPDATE_CONFIRM =
  "Ads you already made keep the earlier version. New ads use this one.";

const LINE = "m-0 text-sm text-slate-700";
const LINK = "inline-flex min-h-[44px] items-center underline md:min-h-0";

/** Where **View it** leads: the object's page, wearing each artwork the
 *  design has on a label or screen area, so **Save this look** keeps it. */
export function viewItPath(id: string, dressing: ModelDressing): string {
  const worn = Object.entries(dressing.skins)
    .filter(([, skin]) => typeof skin === "string" && skin !== "")
    .map(
      ([slot, skin]) =>
        `skin=${encodeURIComponent(slot)}:${encodeURIComponent(skin)}`,
    );
  const path = `/assets/shape/${encodeURIComponent(id)}`;
  return worn.length > 0 ? `${path}?${worn.join("&")}` : path;
}

export interface AddToLibrary {
  /** **Add to Objects**, "Adding…" or **Update in Objects**;
   *  null when the design is in the 3D library as it is, or has no built
   *  step. With `menu` it is one item of the ⋯ menu (a phone), and
   *  `onPressed` closes the menu. */
  button(menu?: { onPressed(): void }): ReactNode;
  /** What came of it: "In Objects." with its two links, the failure
   *  sentence with **Try again**, or why the request was refused. */
  status: ReactNode;
  /** The window **Add to Objects** opens. */
  dialog: ReactNode;
}

/** Adding a design to the 3D library, and updating it there
 *  ([design-studio §11.9], [ux §8.17]). `dressing` is the page's own copy of
 *  the design's surfaces. The three parts are placed by the page: the button
 *  top right (in the ⋯ menu on a phone), the status beside it (under the
 *  header on a phone), the dialog anywhere. For a sculpted design the window
 *  is the texture review ([sculpted §5.4]), also before an update. */
export function useAddToLibrary(
  detail: ModelDetail,
  dressing: ModelDressing,
): AddToLibrary {
  const publish = usePublish(detail.id);
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  // Why the last request was refused (a full library, a busy design).
  const [refused, setRefused] = useState<unknown>(null);

  const { published, current } = detail;
  // A sculpted design is looked at before it is added, and again before an
  // update: its texture may carry painted-in light ([sculpted §5.4]).
  const sculpted = detail.engine === "mesh";
  const working = detail.state === "working";
  const publishing = detail.state === "publishing" || publish.isPending;
  const hasArtwork = Object.keys(dressing.skins).length > 0;

  const send = (body: { name?: string }, after?: () => void) => {
    setRefused(null);
    publish.mutate(body, { onSuccess: after, onError: setRefused });
  };
  const onAdd = () => {
    setRefused(null);
    setName(detail.name.slice(0, OBJECT_NAME_MAX));
    setOpen(true);
  };
  const onUpdate = () => {
    if (sculpted) onAdd();
    else if (window.confirm(UPDATE_CONFIRM)) send({});
  };

  const label = !current
    ? null
    : publishing
      ? ADDING
      : published === null
        ? ADD_TITLE
        : published.up_to_date
          ? null
          : UPDATE_TITLE;
  const onPress = published === null ? onAdd : onUpdate;
  const disabled = publishing || working;
  const title = working ? WAIT_TITLE : undefined;

  const button = (menu?: { onPressed(): void }): ReactNode => {
    if (label === null) return null;
    if (menu) {
      return (
        <button
          type="button"
          role="menuitem"
          className="min-h-[44px] rounded px-2 py-1.5 text-left font-semibold hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          title={title}
          aria-busy={publishing ? true : undefined}
          onClick={() => {
            menu.onPressed();
            onPress();
          }}
        >
          {label}
        </button>
      );
    }
    return (
      <Button
        type="button"
        variant="primary"
        className="disabled:cursor-not-allowed disabled:opacity-50"
        disabled={disabled}
        title={title}
        aria-busy={publishing ? true : undefined}
        onClick={onPress}
      >
        {label}
      </Button>
    );
  };

  const failed = detail.publish_error_code !== null && !publishing;
  const status =
    current && (published !== null || failed || (refused && !open)) ? (
      <>
        {published !== null ? (
          <p
            role="status"
            className={`${LINE} flex flex-wrap items-center gap-x-3`}
            data-testid="in-library"
          >
            <span>{IN_LIBRARY}</span>
            <Link
              to={viewItPath(published.id, dressing)}
              state={{ added: true }}
              className={LINK}
            >
              View it
            </Link>
            <Link
              to={`/new?pin=shape:${encodeURIComponent(published.id)}`}
              className={LINK}
            >
              Use in a new ad
            </Link>
          </p>
        ) : null}
        {failed ? (
          <p
            role="alert"
            className={`${LINE} flex flex-wrap items-center gap-2`}
          >
            <span>{rowMessageFor("publish_failed")}</span>
            <Button
              type="button"
              size="sm"
              className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
              disabled={working}
              title={title}
              onClick={() => send({})}
            >
              Try again
            </Button>
          </p>
        ) : null}
        {refused && !open ? (
          <ErrorLine error={refused} context="design" />
        ) : null}
      </>
    ) : null;

  const typed = name.trim();
  const dialog = (
    <Dialog
      open={open}
      onClose={() => {
        if (!publish.isPending) setOpen(false);
      }}
      title={ADD_TITLE}
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            disabled={publish.isPending}
            onClick={() => {
              setOpen(false);
              // It spends nothing by itself: the panel's button does.
              if (sculpted) focusTryAgain();
            }}
          >
            {sculpted ? "Try again" : "Cancel"}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            className="disabled:cursor-not-allowed disabled:opacity-50"
            disabled={publish.isPending || !typed}
            aria-busy={publish.isPending ? true : undefined}
          >
            {sculpted ? "Add it" : "Add"}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!typed || publish.isPending) return;
          send({ name: typed }, () => setOpen(false));
        }}
      >
        {sculpted ? <ReviewPictures detail={detail} /> : null}
        <Field label="Name">
          <Input
            required
            maxLength={OBJECT_NAME_MAX}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <p className={LINE}>{ADD_TEXT}</p>
        {hasArtwork ? <p className={LINE}>{ADD_ARTWORK_TEXT}</p> : null}
        {refused ? <ErrorLine error={refused} context="design" /> : null}
      </form>
    </Dialog>
  );

  return { button, status, dialog };
}
